import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Trash2 } from 'lucide-react'

import {
  deleteBibleCharacter,
  deleteBibleCostume,
  deleteBibleLocation,
  deleteBibleProp,
  getBibleStyle,
  getCollaborationEligibility,
  getProduction,
  listBibleCharacters,
  listBibleCostumes,
  listBibleLocations,
  listBibleProps,
  listDepartments,
  listMembers,
  saveBibleStyle,
  type BibleStyle,
  type CollaborationEligibility,
  type Production,
  type ProductionDepartment,
  type ProductionMember,
} from '@/api/studio-production'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar, CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { resolveCollaborationGate } from '@/features/studio/lib/collaboration-gate'
import { useAuth } from '@/hooks'
import { useAssignmentScopes } from '@/features/studio/hooks/useAssignmentScopes'
import { BibleEntryModal } from '@/features/studio/modals/BibleEntryModal'
import { BibleEntryCard } from '@/features/studio/pages/bible/BibleEntryCard'
import {
  bibleKindLabelKey,
  bibleAssignmentItemTypeForKind,
  bibleRefPublicIdForEntry,
  deleteConfirmKey,
  styleToEntries,
  styleFieldFromEntryId,
  firstEmptyStyleField,
  styleFieldNameKey,
  type BibleStyleField,
  toBibleEntry,
  type BibleEntryKind,
  type BibleEntryLike,
} from '@/features/studio/pages/bible/bible-utils'
import { ProductionEmptyPanel, ProtoInput, ProtoPagination, ProtoTabs } from '@/features/studio/components/ui-kit'

type Tab = 'characters' | 'locations' | 'props' | 'costumes' | 'style'

const BIBLE_PAGE_SIZE = 16

export function BiblePage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const { user } = useAuth()

  const [tab, setTab] = useState<Tab>('characters')
  const [items, setItems] = useState<BibleEntryLike[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [style, setStyle] = useState<BibleStyle | null>(null)
  const [styleFieldTarget, setStyleFieldTarget] = useState<BibleStyleField | null>(null)
  const [seriesOutline, setSeriesOutline] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savedFlash, setSavedFlash] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<BibleEntryLike | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{
    kind: BibleEntryKind
    id: string
    name: string
  } | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [production, setProduction] = useState<Production | null>(null)
  const [members, setMembers] = useState<ProductionMember[]>([])
  const [departments, setDepartments] = useState<ProductionDepartment[]>([])
  const [eligibility, setEligibility] = useState<CollaborationEligibility | null>(null)

  const gate = useMemo(
    () => resolveCollaborationGate(production, members, eligibility, user?.id),
    [production, members, eligibility, user?.id],
  )

  const isOwner =
    !!production?.is_owner ||
    (production?.user_id != null && user?.id != null && production.user_id === user.id)
  const { canEdit: canEditScope } = useAssignmentScopes(productionId, isOwner)

  const entryKind: BibleEntryKind =
    tab === 'style'
      ? 'style'
      : tab === 'characters'
        ? 'character'
        : tab === 'locations'
          ? 'location'
          : tab === 'props'
            ? 'prop'
            : 'costume'

  const listParams = useMemo(
    () => ({
      limit: BIBLE_PAGE_SIZE,
      offset: page * BIBLE_PAGE_SIZE,
      q: searchQuery || undefined,
    }),
    [page, searchQuery],
  )

  const loadMeta = useCallback(async () => {
    try {
      const [prod, memRes, deptRes, elig] = await Promise.all([
        getProduction(productionId).catch(() => null),
        listMembers(productionId, { limit: 500, offset: 0 }).catch(() => ({
          items: [] as ProductionMember[],
        })),
        listDepartments(productionId).catch(() => ({ items: [] as ProductionDepartment[] })),
        getCollaborationEligibility(productionId).catch(() => null),
      ])
      setProduction(prod)
      setMembers(memRes.items)
      setDepartments(deptRes.items)
      setEligibility(elig)
      setSeriesOutline(prod?.series_outline ?? '')
    } catch {
      /* meta 失败不阻断列表 */
    }
  }, [productionId])

  const loadItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (tab === 'style') {
        const s = await getBibleStyle(productionId)
        setStyle(s)
        const entries = styleToEntries(s, t)
        setItems(entries)
        setTotal(entries.length)
        return
      }

      const fetchList =
        tab === 'characters'
          ? listBibleCharacters
          : tab === 'locations'
            ? listBibleLocations
            : tab === 'props'
              ? listBibleProps
              : listBibleCostumes

      const res = await fetchList(productionId, listParams)
      setItems(res.items.map(toBibleEntry))
      setTotal(res.total)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'bible.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, tab, listParams, t])

  useEffect(() => {
    void loadMeta()
  }, [loadMeta])

  useEffect(() => {
    void loadItems()
  }, [loadItems])

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setPage(0)
  }, [tab, searchQuery])

  useEffect(() => {
    setSearchInput('')
    setSearchQuery('')
    setEditing(null)
    setStyleFieldTarget(null)
    setModalOpen(false)
  }, [tab])

  const canAddStyleField = tab !== 'style' || firstEmptyStyleField(style) != null

  function openCreateModal() {
    if (tab === 'style') {
      const field = firstEmptyStyleField(style)
      if (!field) return
      setStyleFieldTarget(field)
      setEditing(null)
    } else {
      setStyleFieldTarget(null)
      setEditing(null)
    }
    setModalOpen(true)
  }

  function openEditModal(entry: BibleEntryLike) {
    if (tab === 'style') {
      setStyleFieldTarget(styleFieldFromEntryId(entry.public_id))
    } else {
      setStyleFieldTarget(null)
    }
    setEditing(entry)
    setModalOpen(true)
  }

  async function handleSaved() {
    setSavedFlash(true)
    window.setTimeout(() => setSavedFlash(false), 2000)
    await loadItems()
    if (tab === 'style') {
      const s = await getBibleStyle(productionId)
      setStyle(s)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      if (deleteTarget.kind === 'character') {
        await deleteBibleCharacter(productionId, deleteTarget.id)
      } else if (deleteTarget.kind === 'location') {
        await deleteBibleLocation(productionId, deleteTarget.id)
      } else if (deleteTarget.kind === 'prop') {
        await deleteBibleProp(productionId, deleteTarget.id)
      } else if (deleteTarget.kind === 'costume') {
        await deleteBibleCostume(productionId, deleteTarget.id)
      } else if (deleteTarget.kind === 'style') {
        const field = styleFieldFromEntryId(deleteTarget.id)
        const current = style ?? {
          art_direction: '',
          color_palette: '',
          style_prompt: '',
          negative_prompt: '',
          reference_urls: [],
          updated_at: '',
        }
        await saveBibleStyle(productionId, {
          art_direction: field === 'art_direction' ? '' : current.art_direction,
          color_palette: field === 'color_palette' ? '' : current.color_palette,
          style_prompt: field === 'style_prompt' ? '' : current.style_prompt,
          negative_prompt: field === 'negative_prompt' ? '' : current.negative_prompt,
          reference_urls: field === 'art_direction' ? [] : current.reference_urls ?? [],
        })
      }
      setDeleteTarget(null)
      await loadItems()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'bible.saveFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  const kindLabel =
    tab === 'style'
      ? t(styleFieldNameKey(firstEmptyStyleField(style) ?? 'art_direction'))
      : t(bibleKindLabelKey(entryKind))
  const showPagination = tab !== 'style' && total > BIBLE_PAGE_SIZE

  return (
    <CatalogListPageShell>
      <CatalogListToolbar
        filters={
          tab !== 'style' ? (
            <div className="relative min-w-[200px] max-w-xs flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <ProtoInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('bible.searchPlaceholder')}
                className="pl-9"
              />
            </div>
          ) : undefined
        }
        actions={
          <CatalogListPrimaryButton onClick={openCreateModal} disabled={!isOwner || !canAddStyleField}>
            <Plus className="h-4 w-4" />
            {t('bible.addEntry', { kind: kindLabel })}
          </CatalogListPrimaryButton>
        }
      />

      {error ? <p className="mb-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      {savedFlash ? <p className="mb-4 text-sm text-emerald-300">{t('bible.saved')}</p> : null}

      <ProtoTabs
        production
        tabs={[
          { id: 'characters' as const, label: t('bible.tab.characters') },
          { id: 'locations' as const, label: t('bible.tab.locations') },
          { id: 'props' as const, label: t('bible.tab.props') },
          { id: 'costumes' as const, label: t('bible.tab.costumes') },
          { id: 'style' as const, label: t('bible.tab.style') },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="mt-4">
        {loading ? (
          <CatalogListSkeleton count={10} />
        ) : items.length === 0 ? (
          <ProductionEmptyPanel>
            <p className="text-sm text-zinc-500">{t('bible.emptyList', { kind: kindLabel })}</p>
            <CatalogListPrimaryButton className="mt-4" onClick={openCreateModal}>
              <Plus className="h-4 w-4" />
              {t('bible.addEntry', { kind: kindLabel })}
            </CatalogListPrimaryButton>
          </ProductionEmptyPanel>
        ) : (
          <>
            <div className={CATALOG_LIST_GRID_CLASS}>
              {items.map((entry) => (
                <BibleEntryCard
                  key={entry.public_id}
                  productionId={productionId}
                  kind={entryKind}
                  entry={entry}
                  onEdit={() => openEditModal(entry)}
                  onDelete={
                    isOwner
                      ? () =>
                          setDeleteTarget({
                            kind: entryKind,
                            id: entry.public_id,
                            name: entry.name,
                          })
                      : undefined
                  }
                  canEditEntry={canEditScope(
                    bibleAssignmentItemTypeForKind(entryKind),
                    bibleRefPublicIdForEntry(entryKind, entry.public_id),
                  )}
                  canAssignWork={gate.canAssignWork}
                  gate={gate}
                  members={members}
                  departments={departments}
                />
              ))}
            </div>
            {showPagination ? (
              <ProtoPagination
                page={page}
                pageSize={BIBLE_PAGE_SIZE}
                total={total}
                onPageChange={setPage}
                prevLabel={t('pagination.prev')}
                nextLabel={t('pagination.next')}
              />
            ) : null}
          </>
        )}
      </div>

      <BibleEntryModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setStyleFieldTarget(null)
        }}
        productionId={productionId}
        kind={entryKind}
        seriesOutline={seriesOutline}
        editing={editing}
        styleSnapshot={style}
        styleField={styleFieldTarget}
        onSaved={() => void handleSaved()}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('bible.delete')}
        message={
          deleteTarget
            ? deleteTarget.kind === 'style'
              ? t('bible.deleteStyleFieldConfirm', { name: deleteTarget.name })
              : t(deleteConfirmKey(deleteTarget.kind), { name: deleteTarget.name })
            : null
        }
        icon={Trash2}
        destructive
        busy={deleteBusy}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (deleteBusy) return
          setDeleteTarget(null)
        }}
      />
    </CatalogListPageShell>
  )
}
